import { useCallback, useEffect, type DragEvent } from 'react'
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  useReactFlow,
  type IsValidConnection,
  type Node,
  type OnBeforeDelete,
  type OnNodeDrag,
  type Viewport,
} from '@xyflow/react'
import {
  collectDescendants,
  getAbsolutePosition,
  NODE_DEFAULTS,
  newId,
  sortParentsFirst,
  useFlowStore,
} from '../store/flowStore'
import { nodeTypes } from './nodes'
import { edgeTypes } from './edges/LabeledEdge'
import { ConnectionLine } from './ConnectionLine'
import { segmentIntersectsRect, type Rect } from '../utils/geometry'
import type { AppEdge, AppNode, NodeKind } from '../types'

function nodeRect(node: AppNode, nodes: AppNode[]): Rect {
  const abs = getAbsolutePosition(node, nodes)
  const defaults = NODE_DEFAULTS[(node.type ?? 'process') as NodeKind]
  return {
    x: abs.x,
    y: abs.y,
    width: node.measured?.width ?? node.width ?? defaults.width,
    height: node.measured?.height ?? node.height ?? defaults.height,
  }
}

function nodeCenter(node: AppNode, nodes: AppNode[]) {
  const rect = nodeRect(node, nodes)
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }
}

/** Encontra o grupo mais interno que contém o ponto, ignorando os ids informados. */
function findGroupAt(
  point: { x: number; y: number },
  nodes: AppNode[],
  ignoreIds: Set<string>,
): AppNode | undefined {
  let found: AppNode | undefined
  for (const node of nodes) {
    if (node.type !== 'group' || ignoreIds.has(node.id)) continue
    const rect = nodeRect(node, nodes)
    if (
      point.x >= rect.x &&
      point.x <= rect.x + rect.width &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.height
    ) {
      found = node
    }
  }
  return found
}

const isValidConnection: IsValidConnection<AppEdge> = (connection) => {
  if (!connection.source || !connection.target) return false
  if (connection.source === connection.target) return false
  const { nodes, edges } = useFlowStore.getState()
  const targetNode = nodes.find((n) => n.id === connection.target)
  const sourceNode = nodes.find((n) => n.id === connection.source)
  if (targetNode?.type === 'annotation' || sourceNode?.type === 'annotation') {
    return false
  }
  return !edges.some(
    (e) =>
      e.source === connection.source &&
      e.target === connection.target &&
      (e.sourceHandle ?? null) === (connection.sourceHandle ?? null) &&
      (e.targetHandle ?? null) === (connection.targetHandle ?? null),
  )
}

function minimapColor(node: Node): string {
  const data = node.data as AppNode['data'] | undefined
  return (
    data?.color ?? NODE_DEFAULTS[(node.type ?? 'process') as NodeKind].color
  )
}

export function FlowCanvas() {
  const nodes = useFlowStore((s) => s.nodes)
  const edges = useFlowStore((s) => s.edges)
  const colorMode = useFlowStore((s) => s.colorMode)
  const presentationMode = useFlowStore((s) => s.presentationMode)
  const readOnly = useFlowStore((s) => s.readOnly)
  const viewport = useFlowStore((s) => s.viewport)
  const onNodesChange = useFlowStore((s) => s.onNodesChange)
  const onEdgesChange = useFlowStore((s) => s.onEdgesChange)
  const onConnect = useFlowStore((s) => s.onConnect)
  const takeSnapshot = useFlowStore((s) => s.takeSnapshot)

  const locked = presentationMode || readOnly
  const { screenToFlowPosition } = useReactFlow()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (useFlowStore.getState().presentationMode || useFlowStore.getState().readOnly) {
        return
      }
      const target = event.target as HTMLElement
      if (target.closest('input, textarea, select, [contenteditable]')) return
      const mod = event.ctrlKey || event.metaKey
      if (!mod) return
      const key = event.key.toLowerCase()
      const store = useFlowStore.getState()
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault()
        store.undo()
      } else if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault()
        store.redo()
      } else if (key === 'c') {
        event.preventDefault()
        store.copySelection()
      } else if (key === 'v') {
        event.preventDefault()
        store.pasteClipboard()
      } else if (key === 'd') {
        event.preventDefault()
        store.duplicateNodes(
          store.nodes.filter((n) => n.selected).map((n) => n.id),
        )
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      const state = useFlowStore.getState()
      if (state.presentationMode || state.readOnly) return
      const kind = event.dataTransfer.getData(
        'application/reactflow',
      ) as NodeKind
      if (!kind || !(kind in NODE_DEFAULTS)) return

      const defaults = NODE_DEFAULTS[kind]
      const point = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })
      let position = {
        x: point.x - defaults.width / 2,
        y: point.y - defaults.height / 2,
      }

      let parentId: string | undefined
      if (kind !== 'group') {
        const { nodes: current } = useFlowStore.getState()
        const group = findGroupAt(point, current, new Set())
        if (group) {
          const groupAbs = getAbsolutePosition(group, current)
          parentId = group.id
          position = {
            x: position.x - groupAbs.x,
            y: position.y - groupAbs.y,
          }
        }
      }

      useFlowStore.getState().addNode(kind, position, parentId)
    },
    [screenToFlowPosition],
  )

  const onNodeDragStart: OnNodeDrag<AppNode> = useCallback(() => {
    takeSnapshot()
  }, [takeSnapshot])

  const onNodeDragStop: OnNodeDrag<AppNode> = useCallback(
    (_event, _node, draggedNodes) => {
      const store = useFlowStore.getState()
      if (store.readOnly || store.presentationMode) return
      let currentNodes = [...store.nodes]
      let currentEdges = store.edges
      let changed = false

      for (const dragged of draggedNodes) {
        const node = currentNodes.find((n) => n.id === dragged.id)
        if (!node) continue
        const ignore = collectDescendants([node.id], currentNodes)
        const center = nodeCenter(node, currentNodes)
        const group = findGroupAt(center, currentNodes, ignore)

        if (group && node.parentId !== group.id) {
          const abs = getAbsolutePosition(node, currentNodes)
          const groupAbs = getAbsolutePosition(group, currentNodes)
          currentNodes = currentNodes.map((n) =>
            n.id === node.id
              ? {
                  ...n,
                  parentId: group.id,
                  position: { x: abs.x - groupAbs.x, y: abs.y - groupAbs.y },
                }
              : n,
          )
          changed = true
        } else if (!group && node.parentId) {
          const abs = getAbsolutePosition(node, currentNodes)
          currentNodes = currentNodes.map((n) =>
            n.id === node.id
              ? { ...n, parentId: undefined, position: abs }
              : n,
          )
          changed = true
        }
      }

      if (draggedNodes.length === 1) {
        const node = currentNodes.find((n) => n.id === draggedNodes[0].id)
        const isConnectable =
          node && node.type !== 'group' && node.type !== 'annotation'
        const hasEdges =
          node &&
          currentEdges.some(
            (e) => e.source === node.id || e.target === node.id,
          )

        if (node && isConnectable && !hasEdges) {
          const rect = nodeRect(node, currentNodes)
          const hitEdge = currentEdges.find((edge) => {
            const source = currentNodes.find((n) => n.id === edge.source)
            const target = currentNodes.find((n) => n.id === edge.target)
            if (!source || !target) return false
            return segmentIntersectsRect(
              nodeCenter(source, currentNodes),
              nodeCenter(target, currentNodes),
              rect,
            )
          })

          if (hitEdge) {
            const shared = {
              type: 'labeled' as const,
              markerEnd: hitEdge.markerEnd,
              animated: hitEdge.animated,
              style: hitEdge.style,
            }
            const pathType = hitEdge.data?.pathType ?? 'smoothstep'
            currentEdges = [
              ...currentEdges.filter((e) => e.id !== hitEdge.id),
              {
                ...shared,
                id: newId(),
                source: hitEdge.source,
                sourceHandle: hitEdge.sourceHandle,
                target: node.id,
                data: { label: hitEdge.data?.label ?? '', pathType },
              },
              {
                ...shared,
                id: newId(),
                source: node.id,
                target: hitEdge.target,
                targetHandle: hitEdge.targetHandle,
                data: { label: '', pathType },
              },
            ]
            changed = true
          }
        }
      }

      if (changed) {
        store.setNodes(sortParentsFirst(currentNodes))
        store.setEdges(currentEdges)
      }
    },
    [],
  )

  const onBeforeDelete: OnBeforeDelete<AppNode, AppEdge> = useCallback(
    async ({ nodes: nodesToDelete, edges: edgesToDelete }) => {
      const store = useFlowStore.getState()
      if (store.readOnly || store.presentationMode) {
        return false
      }
      store.takeSnapshot()
      const ids = collectDescendants(
        nodesToDelete.map((n) => n.id),
        store.nodes,
      )
      const allNodes = store.nodes.filter((n) => ids.has(n.id))
      const edgeIds = new Set(edgesToDelete.map((e) => e.id))
      const allEdges = store.edges.filter(
        (e) => edgeIds.has(e.id) || ids.has(e.source) || ids.has(e.target),
      )
      return { nodes: allNodes, edges: allEdges }
    },
    [],
  )

  const onMoveEnd = useCallback((_event: unknown, nextViewport: Viewport) => {
    useFlowStore.getState().setViewportState(nextViewport)
  }, [])

  return (
    <div className="flow-canvas">
      <ReactFlow<AppNode, AppEdge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStart={onNodeDragStart}
        onSelectionDragStart={takeSnapshot}
        onNodeDragStop={onNodeDragStop}
        onBeforeDelete={onBeforeDelete}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onMoveEnd={onMoveEnd}
        isValidConnection={isValidConnection}
        connectionMode={ConnectionMode.Loose}
        connectionLineComponent={ConnectionLine}
        connectionRadius={40}
        nodesDraggable={!locked}
        nodesConnectable={!locked}
        elementsSelectable={!locked}
        edgesReconnectable={!locked}
        snapToGrid
        snapGrid={[8, 8]}
        colorMode={colorMode}
        deleteKeyCode={locked ? null : ['Delete', 'Backspace']}
        selectionKeyCode="Shift"
        multiSelectionKeyCode={['Control', 'Meta']}
        panOnScroll
        zoomOnDoubleClick={false}
        minZoom={0.1}
        maxZoom={2.5}
        defaultViewport={viewport ?? undefined}
        fitView={!viewport}
        fitViewOptions={{ padding: 0.15 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1.4} />
        <Controls position="bottom-left" />
        <MiniMap
          position="bottom-right"
          nodeColor={minimapColor}
          nodeStrokeWidth={3}
          pannable
          zoomable
        />
        <Panel position="top-center" className="stats-panel">
          {nodes.length} nós · {edges.length} conexões
        </Panel>
      </ReactFlow>
    </div>
  )
}
